import React, { useEffect, useState, useMemo } from 'react';
import { getLocale } from 'umi';
import * as echarts from 'echarts';
import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import './index.scss';

interface IProps {
  uniqueKey: string;
  data: any[];
  getAddedParams: Function;
  getRenderContent: Function;
  getTooltip: Function;
  metric: string;
}

interface ITreeMapData {
  name: string;
  label: string;
  itemStyle: {
    color: string;
  };
  value: number;
  isShow: boolean;
  [key: string]: any;
}

export default function RectangleTree(props: IProps) {
  const { uniqueKey, data, getAddedParams, getRenderContent, getTooltip, metric } = props;

  const locale = getLocale();
  const [treeMapData, setTreeMapData] = useState<ITreeMapData[]>([]);

  const id = `dbi-rectangle-tree__${uniqueKey}`;

  const handleLegendClick = (clickItem: ITreeMapData) => {
    const index = treeMapData.findIndex(item => item.name === clickItem.name);
    const _tmpData = [...treeMapData];
    _tmpData.splice(index, 1, {
      ...clickItem,
      isShow: !clickItem.isShow
    });
    setTreeMapData(_tmpData);
    renderChart(id, _tmpData.filter(item => item.isShow));
  };

  const renderChart = (containId: string, data: ITreeMapData[], nodeClick = false) => {
    const getSeriesConfig = (_list: ITreeMapData[]) => {
      return [
        {
          name: 'option',
          type: 'treemap',
          visibleMin: 300,
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          drillDownIcon: '',
          roam: false,
          nodeClick,
          breadcrumb: { show: false },
          data: _list,
          leafDepth: 1,
          levels: [
            {
              itemStyle: {
                borderColor: 'rgba(255,255,255,0.4)',
                gapWidth: 2,
              },
            },
            {
              colorSaturation: [0.3, 0.6],
              itemStyle: {
                borderColor: 'rgba(255,255,255,0.4)',
                gapWidth: 2,
              },
            },
          ],
        },
      ];
    };

    const chartDom = document.getElementById(containId);
    if (chartDom) {
      let myChart = echarts.getInstanceByDom(chartDom);
      if (myChart == null) {
        myChart = echarts.init(chartDom);
      }
      const option = {
        title: {
          show: false,
        },
        tooltip: {
          // trigger: 'axis',
          padding: 0,
          borderWidth: 0,
          formatter: (config: any) => {
            return getTooltip(config?.data || {});
          },
          backgroundColor: 'rgba(0,0,0,0)',
        },
        legend: {
          show: false,
        },
        series: getSeriesConfig(data),
      };
      option && myChart.setOption(option, true);
      myChart.resize();
    }
  };

  const getChartData = (): ITreeMapData[] => {
    const getLabelOpt = () => {
      return {
        position: 'insideTopLeft',
        formatter: (config) => {
          return getRenderContent(config?.data || {});
        },
        rich: {
          name: {
            fontSize: 12,
            color: '#fff',
            padding: [10, 0, 0, 10],
          },
          num: {
            fontSize: 16,
            color: '#fff',
            padding: [9, 0, 9, 10],
          },
          precent: {
            fontSize: 16,
            color: '#fff',
            padding: [0, 0, 0, 10],
          },
        },
      };
    };
    return data?.map((item, index) => {
      return {
        label: getLabelOpt(),
        itemStyle: {
          color: ECHARTSCOLOR[index],
        },
        isShow: true,
        ...getAddedParams(item, locale),
      };
    });
  };

  const initial = () => {
    if (data.length > 0) {
      const treeMapData = getChartData();
      setTreeMapData(treeMapData);
      renderChart(id, treeMapData);
    }
  };

  useEffect(() => {
    initial();
  }, [metric, data.length, locale]);

  return (
    <div className="dbi-rectangle-tree">
      <div id={id} className="dbi-rectangle-tree-chart"></div>
      {
        <div className="dbi-rectangle-tree-legend">
          {treeMapData?.map((item, index) => {
            const { name, itemStyle, isShow } = item;
            return (
              <div
                className="color-item"
                key={name}
                onClick={() => handleLegendClick(item)}
                style={{
                  opacity: isShow ? 1 : 0.3,
                  cursor: 'pointer'
                }}
              >
                <div className="map-point" style={{ backgroundColor: itemStyle.color }}></div>
                <div className="map-title">{name}</div>
              </div>
            );
          })}
        </div>
      }
    </div>
  );
}
